"use client";

import {
  formatMobileForDisplay,
  getSessionData,
  redirectWithUtmParam,
} from "@repo/shared-utils/common-utils";
import Conditional from "@repo/shared-ui/Conditional";
import BFLModal from "@repo/shared-ui/BFLModal";
import { useState, useEffect } from "react";
import {
  useAppSelector,
  selectIsAuthSuccessOrPerpetual,
  selectLeadStatus,
  selectLoginStatus,
  useAppDispatch,
  setLeadStatus,
  LeadStatus,
} from "@repo/global-store";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { consentInsert, consentRead } from "@repo/sso";
import { leadsBuySell } from "@repo/sso/leadsApi";

import BFLOtpSendModal from "@repo/sso/BFLOtpSendModal";
import BFLOtpVerifyModal from "@repo/sso/BFLOtpVerifyModal";
import BFLConsentModal from "@repo/sso/BFLConsentModal";
import FlowButton from "@repo/shared-ui/FlowButton";
import { CombinedClevertapProps, dataLayer } from "@repo/analytics/data-layer-utils";
import { isOpenInApp } from "@repo/shared-utils/pwa-utils";
import { Heading, IAmcSsoModalProperties } from "../../interfaces/IAMCContent.interface";

type ConsentInsertResponse = {
  statusCode?: number;
  status?: string;
  message?: string;
  data?: {
    mobileNumber?: string | number;
  };
};

export const submitConsentInsert = async (): Promise<ConsentInsertResponse> => {
  const consentResponse = (await consentInsert(
    "BFSL",
    "BFL Entity",
    true
  )) as ConsentInsertResponse;
  return consentResponse;
};

const InvestNowFlow = ({
  amcName,
  headingContent,
  ssoContent,
}: {
  amcName?: string;
  headingContent?: Heading;
  ssoContent: IAmcSsoModalProperties;
}) => {
  const { ConsentModal, otpSendModal, verifyOtp, flow } = ssoContent ?? {};
  const {
    redirectPTBlabel = "Resume Journey",
    redirectlabel = "Open MF Account",
    redirectETBlabe = "Invest Now",
    redirectlink = "",
    redirectApplink = "",
  } = headingContent ?? {};

  const isAuthSuccessOrPerpetual = useAppSelector(selectIsAuthSuccessOrPerpetual);
  const leadStatus = useAppSelector(selectLeadStatus);
  const loginStatus = useAppSelector(selectLoginStatus);
  const appDispatch = useAppDispatch();

  const [openModal, setOpenModal] = useState<"consent" | "send" | "verify" | "none">("none");
  const [consentProvided, setConsentProvided] = useState<boolean>(false);
  const [sessionMobileNo, setSessionMobileNo] = useState<string | undefined>(undefined);
  const [buttonText, setButtonText] = useState<string | undefined>(redirectlabel);

  const [showStickyCTA, setShowStickyCTA] = useState(false);

  /**
   * getConsentReadData
   */
  const getConsentReadData = async () => {
    try {
      const consentReadData = await consentRead("BFSL", "34", "BFL Entity");
      const hasConsent = consentReadData?.data?.consentValue === "2";
      setConsentProvided(hasConsent);
      return hasConsent;
    } catch {
      setConsentProvided(false);
      return false;
    }
  };

  useEffect(() => {
    if (leadStatus === null) {
      console.warn("Lead status is null");
      return;
    }
    getConsentReadData();
    if (leadStatus === "Inprogress") {
      setButtonText(redirectPTBlabel);
    } else if (leadStatus === "ETB") {
      setButtonText(redirectETBlabe);
    } else {
      setButtonText(redirectlabel);
    }
  }, [leadStatus, redirectETBlabe, redirectPTBlabel, redirectlabel]);

  useEffect(() => {
    if (!isAuthSuccessOrPerpetual) {
      console.warn("Not fetching lead status as user is not logged in or perpetual");
      return;
    }

    const getLeadsData = async () => {
      const { mobileNo } = await getSessionData();

      if (mobileNo) {
        const leadsData = await leadsBuySell(mobileNo);

        if (leadsData?.statusCode === 90) {
          appDispatch(
            setLeadStatus({
              leadStatus: leadsData.data?.flag as LeadStatus,
            })
          );
        } else {
          appDispatch(
            setLeadStatus({
              leadStatus: "" as LeadStatus,
            })
          );
        }
      }
    };

    getLeadsData();
  }, [appDispatch, isAuthSuccessOrPerpetual]);

  // Get mobile number from session data
  useEffect(() => {
    let hasMounted = true;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const fetchSessionMobileNumber = async (rc = 0) => {
      try {
        const { mobileNo } = await getSessionData();
        if (mobileNo) {
          if (hasMounted) setSessionMobileNo(mobileNo);
        } else if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        }
      } catch (e) {
        if (rc < 10 && hasMounted) {
          retryTimeoutId = setTimeout(() => fetchSessionMobileNumber(rc + 1), 500);
        } else {
          console.warn("Error fetching session data:", e);
        }
      }
    };

    fetchSessionMobileNumber();

    return () => {
      hasMounted = false;
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
    };
  }, []);

  useEffect(() => {
    const heading = document.getElementById("Heading");
    if (!heading) return;

    const handleScroll = () => {
      setShowStickyCTA(heading.getBoundingClientRect().bottom < 0);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleOtpValidationSuccess = () => {
    setOpenModal("none");
  };

  const handleOtpValidationFailure = () => {
    // TODO - Keep modal open for retry
  };

  const handleCTAClick = async (label: string = "") => {
    const payload = {
      component: "amc_heading_bottom_cta",
      element: "cta_strip_card_button_click",
      event: "click",
      event_type: "button_click",
      destination_url: "",
      section_title: amcName,
      cta_text: label,
      product_name: "bajaj_mutual_fund",
      category_name: "loan",
      page_type: "pdp",
      customer_tag: loginStatus,
      is_loggedin: loginStatus,
      component_position: "",
      component_id: `{${amcName}/similar_funds`,
    };
    await dataLayer(loginStatus, payload, payload as unknown as CombinedClevertapProps);
  };

  const getModalToOpen = async () => {
    if (consentProvided) {
      return "send";
    }

    const hasExistingConsent = await getConsentReadData();
    return hasExistingConsent ? "send" : "consent";
  };

  return (
    <>
      <Conditional
        if={(isAuthSuccessOrPerpetual && leadStatus === null) || loginStatus === "pending"}
      >
        <BFLSkeleton width={225} count={1} height={40} />
      </Conditional>

      <Conditional
        if={
          (isAuthSuccessOrPerpetual &&
            (leadStatus === "" || leadStatus === "null" || leadStatus === "Inprogress")) ||
          loginStatus === "failure"
        }
      >
        <FlowButton
          buttonText={buttonText}
          onClick={async () => {
            await handleCTAClick(buttonText);
            if (!isAuthSuccessOrPerpetual) {
              redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
              return;
            }

            const modalToOpen = await getModalToOpen();
            setOpenModal(modalToOpen);
          }}
        />
      </Conditional>

      {/* Invest Now */}
      <Conditional if={isAuthSuccessOrPerpetual && leadStatus === "ETB"}>
        <FlowButton
          buttonText={redirectlabel}
          onClick={async () => {
            await handleCTAClick(redirectlabel);
            redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
          }}
        />
      </Conditional>

      {showStickyCTA && (
        <div className="fixed right-0 bottom-0 z-[6] p-3 md:hidden">
          <FlowButton
            buttonText={
              isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText
            }
            onClick={async () => {
              const label =
                isAuthSuccessOrPerpetual && leadStatus === "ETB" ? redirectlabel : buttonText;

              await handleCTAClick(label);

              if (leadStatus !== "Inprogress") {
                redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
                return;
              }

              const modalToOpen = await getModalToOpen();
              setOpenModal(modalToOpen);
            }}
          />
        </div>
      )}
      <Conditional if={openModal === "consent"}>
        <BFLModal
          data-testid="bfl-modal"
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title="Redirecting you to Bajaj Financial Securities Limited"
          secondaryButtonText="Skip"
          onSecondaryButtonClick={() => {
            setOpenModal("none");
            redirectWithUtmParam(isOpenInApp() ? redirectApplink : redirectlink, "false");
          }}
          onPrimaryButtonClick={async () => {
            try {
              const consentResponse = await submitConsentInsert();

              if (consentResponse.statusCode === 90 && consentResponse.status === "success") {
                setConsentProvided(true);
                const mobileNumber = consentResponse.data?.mobileNumber;
                if (typeof mobileNumber === "string" || typeof mobileNumber === "number") {
                  setSessionMobileNo(String(mobileNumber));
                }
                setOpenModal("send");
              } else {
                console.error("Consent API failed:", consentResponse.message);
                setConsentProvided(false);
              }
            } catch (error) {
              console.error("Error in consent submission:", error);
              setConsentProvided(false);
            }
          }}
          enableScroll={false}
          buttonText="Continue"
        >
          <BFLConsentModal ssoContent={ConsentModal} />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "send"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          onPrimaryButtonClick={() => {
            setOpenModal("verify");
          }}
          title={otpSendModal?.title}
          enableScroll={false}
          subtitle={otpSendModal?.subtitle}
        >
          <BFLOtpSendModal
            ssoContent={otpSendModal}
            setOpenModal={setOpenModal}
            prefillMobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>

      <Conditional if={openModal === "verify"}>
        <BFLModal
          isOpen={true}
          onClose={() => {
            setOpenModal("none");
          }}
          title={verifyOtp?.title}
          subtitle={
            sessionMobileNo
              ? verifyOtp?.subtitle1?.replace(
                  "${mobileNo}",
                  formatMobileForDisplay(sessionMobileNo)
                )
              : verifyOtp?.subtitle2
          }
        >
          <BFLOtpVerifyModal
            ssoContent={verifyOtp}
            onValidationSuccess={handleOtpValidationSuccess}
            onValidationFailure={handleOtpValidationFailure}
            mobileNumber={sessionMobileNo}
          />
        </BFLModal>
      </Conditional>
    </>
  );
};

export default InvestNowFlow;
